import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import FormattedCurrency from 'components/ui/FormattedCurrency/FormattedCurrency';
import { compensationsShape } from 'constants/propTypes';

import { BodyCell, Row, StyledTable, StyledTableBody, StyledTableFooter, StyledTableHead } from './CompensationBreakdown.styles';
import { REFUND_100 } from 'constants/prrRefund';

const SalesforceCompensationBreakdown = ({
  isVoucher,
  isLoyalty,
  isCashTicketRefund,
  currency,
  refundPercentage,
  compensationItems = [],
  compensateLegs = []
}) => {
  let ticketPrice = 0;
  let refundAmount = 0;
  let loyaltyTicketPrice = 0;

  return (
    <StyledTable data-testid="compensation-table">
      <StyledTableHead>
        <tr>
          <th></th>
          <th>
            <FormattedMessage id="prrRefund.passenger" />
          </th>
          <th>
            <FormattedMessage id="prrRefund.salesforceCompensationRecord" />
          </th>
          {isLoyalty && (
            <th>
              <FormattedMessage id="prrRefund.clubEurostarAccount" />
            </th>
          )}
          <th>
            <FormattedMessage id="prrRefund.ticketNumber" />
          </th>
          <th>
            <FormattedMessage id="prrRefund.costPerPerson" />
          </th>
          <th>
            <FormattedMessage id="prrRefund.compensationPerPerson" />
          </th>
        </tr>
      </StyledTableHead>
      <StyledTableBody>
        {compensationItems.map(
          ({
            compensationRecord,
            firstName,
            lastName,
            ticketNumber,
            membershipNumber,
            currency,
            onlinePointsAmount,
            ticketPrice: compensationTicketPrice,
            cashCompensationAmount,
            voucherCompensationAmount,
            loyaltyCompensationAmount,
            loyaltyTicketPrice: compensationLoyaltyPrice
          }) => {
            let ticketRefundAmount = 0;
            const trainNumber = compensateLegs.find((l) => l.products.find((p) => p.ticketNumber === ticketNumber))?.legName || '';

            if (isLoyalty) {
              if (isVoucher) {
                ticketRefundAmount = voucherCompensationAmount;
              } else if (isCashTicketRefund) {
                ticketRefundAmount = onlinePointsAmount;
              } else {
                ticketRefundAmount = loyaltyCompensationAmount;
              }
            } else {
              if (refundPercentage === String(REFUND_100.value)) {
                ticketRefundAmount = compensationTicketPrice;
              } else if (isVoucher) {
                ticketRefundAmount = voucherCompensationAmount;
              } else {
                ticketRefundAmount = cashCompensationAmount;
              }
            }

            ticketPrice += compensationTicketPrice;
            loyaltyTicketPrice += compensationLoyaltyPrice;
            refundAmount += ticketRefundAmount;

            return (
              <tr data-testid={compensationRecord} key={compensationRecord}>
                <td>{trainNumber}</td>
                <td>
                  <BodyCell>
                    <Row>
                      {firstName ?? ''} {lastName ?? ''}
                    </Row>
                  </BodyCell>
                </td>
                <td>
                  <BodyCell>
                    <Row>{compensationRecord}</Row>
                  </BodyCell>
                </td>
                {isLoyalty && (
                  <td>
                    <BodyCell>
                      <Row>{membershipNumber}</Row>
                    </BodyCell>
                  </td>
                )}
                <td>
                  <BodyCell>
                    <Row>{ticketNumber}</Row>
                  </BodyCell>
                </td>
                <td>
                  <BodyCell>
                    <Row>
                      {isLoyalty && !isCashTicketRefund ? (
                        <FormattedMessage id="searchResults.pts" values={{ points: compensationLoyaltyPrice }} />
                      ) : (
                        <FormattedCurrency currency={currency} amount={compensationTicketPrice} />
                      )}
                    </Row>
                  </BodyCell>
                </td>
                <td>
                  <BodyCell>
                    <Row>
                      {isLoyalty && !isVoucher ? (
                        <FormattedMessage id="searchResults.pts" values={{ points: ticketRefundAmount }} />
                      ) : (
                        <FormattedCurrency currency={currency} amount={ticketRefundAmount} />
                      )}
                    </Row>
                  </BodyCell>
                </td>
              </tr>
            );
          }
        )}
      </StyledTableBody>
      <StyledTableFooter>
        <tr>
          <td colSpan={isLoyalty ? 6 : 5} data-testid="compensation-ticket-price">
            <FormattedMessage id="prrRefund.ticketPrice" />:{' '}
            {isLoyalty && !isCashTicketRefund ? (
              <FormattedMessage id="searchResults.pts" values={{ points: +loyaltyTicketPrice.toFixed(2) }} />
            ) : (
              <FormattedCurrency currency={currency} amount={+ticketPrice.toFixed(2)} />
            )}
          </td>
          <td colSpan={1} data-testid="compensation-refund-amount">
            <FormattedMessage id="prrRefund.refundAmount" />:{' '}
            {isLoyalty && !isVoucher ? (
              <FormattedMessage id="searchResults.pts" values={{ points: +refundAmount.toFixed(2) }} />
            ) : (
              <FormattedCurrency currency={currency} amount={+refundAmount.toFixed(2)} />
            )}
          </td>
        </tr>
      </StyledTableFooter>
    </StyledTable>
  );
};

SalesforceCompensationBreakdown.propTypes = {
  isVoucher: PropTypes.bool,
  isLoyalty: PropTypes.bool,
  isCashTicketRefund: PropTypes.bool,
  currency: PropTypes.string,
  refundPercentage: PropTypes.string,
  compensationItems: compensationsShape,
  compensateLegs: PropTypes.arrayOf(
    PropTypes.shape({
      legName: PropTypes.string,
      direction: PropTypes.string,
      products: PropTypes.arrayOf(
        PropTypes.shape({
          itemRef: PropTypes.string,
          passengerType: PropTypes.string,
          bucketCode: PropTypes.string,
          productFamily: PropTypes.shape({
            code: PropTypes.string
          }),
          currency: PropTypes.string,
          loyaltyPoints: PropTypes.number,
          price: PropTypes.number
        })
      )
    })
  )
};

export default SalesforceCompensationBreakdown;
